/**
 * Store del API Log (v13 del mockup).
 *
 * Registra cada interacción con sistemas externos (ERP / SNT) — útil para
 * auditoría regulatoria y debugging. En esta versión React vive en localStorage;
 * en producción server-side con retención por fecha (deuda GAMP 5).
 *
 * Cap LRU a 500 entradas — más allá de eso se trunca silenciosamente
 * (también deuda — ver Handoff).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ApiLogEntry, ApiSistema, HttpMetodo } from '@/domain/types';

const MAX_ENTRIES = 500;

interface ApiLogState {
  entries: ApiLogEntry[];
  counter: number;
  log: (entry: Omit<ApiLogEntry, 'id' | 'ts' | 'ok'>) => ApiLogEntry;
  clear: () => void;
}

export const useApiLogStore = create<ApiLogState>()(
  persist(
    (set, get) => ({
      entries: [],
      counter: 0,
      log: (entry) => {
        const next = get().counter + 1;
        const id = `AL-${String(next).padStart(6, '0')}`;
        const ok = entry.status >= 200 && entry.status < 300;
        const full: ApiLogEntry = {
          id,
          ts: new Date().toISOString(),
          ok,
          ...entry,
        };
        // unshift + cap (LRU: las más viejas se descartan al exceder MAX_ENTRIES)
        const newEntries = [full, ...get().entries].slice(0, MAX_ENTRIES);
        set({ entries: newEntries, counter: next });
        return full;
      },
      clear: () => set({ entries: [], counter: 0 }),
    }),
    {
      name: 'riga-traza-apilog-v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries, counter: state.counter }),
    },
  ),
);

/**
 * Helper para que los endpoints mock o reales puedan loggear fuera del
 * contexto de React (los endpoints son funciones puras).
 */
export function logApi(entry: {
  sistema: ApiSistema;
  metodo: HttpMetodo;
  endpoint: string;
  status: number;
  duracionMs?: number;
  operacionId?: string;
  requestBody?: unknown;
  responseBody?: unknown;
  errorMsg?: string;
}): ApiLogEntry {
  return useApiLogStore.getState().log(entry);
}
